/**
 * Tailwind-scale primitives layer for the Calm Health design system, transcribed
 * from the Figma Foundation Reference. To re-pull values when the Figma source
 * changes, use the `design-tokens-figma-sync` skill (it discovers the section
 * nodes by the board's named column frames) — `src/primitives/index.ts` is the
 * source of truth.
 *
 * These are an IMPLEMENTATION layer — application code should consume the
 * semantics layer, not raw primitives. They are namespaced under `PRIMITIVES`
 * (public, ergonomic) and `TOKENS_PRIMITIVES` (the PascalCase DTCG tree the
 * cross-platform exporter reads). Both assemble from a single set of shared
 * `_src` groups (mostly `leaves()`, plus a few standalone single-leaf nodes like
 * white/black and the font family) so there is one source of truth with no
 * duplicated data.
 *
 * Public access mirrors the Kotlin/Swift emit: digit-leading leaf names are
 * exposed `_`-prefixed (`PRIMITIVES.color.blue._700`, like `Primitives.Color.Blue._700`
 * on mobile), so the same access pattern works across all three platforms.
 *
 * Additive only: the legacy `DS_*` layer is untouched.
 */
import { described, leaves } from '../dtcg';
import { FIGMA_DESCRIPTIONS } from '../figma-descriptions.generated';
function publicValues(group) {
    return Object.fromEntries(Object.entries(group).map(([k, leaf]) => [
        /^[0-9]/.test(k) ? `_${k}` : k,
        leaf.$value,
    ]));
}
// --- Color ramps -----------------------------------------------------------
const COLOR_NEUTRAL_src = leaves('color', {
    '50': '#F6F5F8',
    '100': '#EAEAEF',
    '200': '#D5D4DC',
    '300': '#B5B4C1',
    '400': '#908FA2',
    '500': '#76758B',
    '600': '#5A596E',
    '700': '#3E3D51',
    '800': '#1A1A2C',
    '900': '#0E0D18',
});
const COLOR_BLUE_src = leaves('color', {
    '100': '#E2F6FF',
    '200': '#C4DFFF',
    '300': '#A6CEFF',
    '400': '#6BAEFF',
    '500': '#4A93E5',
    '600': '#2879C9',
    '700': '#0060A9',
    '800': '#004881',
    '900': '#00315B',
    '950': '#001C38',
});
const COLOR_PURPLE_src = leaves('color', {
    '100': '#EEEEFF',
    '200': '#E2DFFF',
    '300': '#C2C1FF',
    '400': '#A3A2FF',
    '500': '#7D7CF2',
    '600': '#6362BF',
    '700': '#504CCB',
    '800': '#3E3B9A',
    '900': '#2D2A78',
    '950': '#1F1D58',
});
const COLOR_RED_src = leaves('color', {
    '100': '#FFFBFF',
    '200': '#FFEDEA',
    '300': '#FFDAD4',
    '400': '#FFB4A8',
    '500': '#FF8A78',
    '600': '#F3614C',
    '700': '#D14836',
    '800': '#AF3021',
    '900': '#680100',
    '950': '#410100',
});
const COLOR_ORANGE_src = leaves('color', {
    '100': '#FDF7F9',
    '200': '#FFEDE4',
    '300': '#FFDCC6',
    '400': '#FFB785',
    '500': '#F3944B',
    '600': '#D47A33',
    '700': '#B4611B',
    '800': '#954A00',
    '900': '#502500',
    '950': '#301400',
});
const COLOR_GREEN_src = leaves('color', {
    '100': '#F8FFEA',
    '200': '#D0FFA4',
    '300': '#85DC33',
    '400': '#6BC00F',
    '500': '#58A300',
    '600': '#488600',
    '700': '#386B00',
    '800': '#295000',
    '900': '#1A3700',
    '950': '#0D2000',
});
const COLOR_CYAN_src = leaves('color', {
    '100': '#E5F3FF',
    '200': '#C7E7FF',
    '300': '#85CFFF',
    '400': '#53B4EC',
    '500': '#3099D0',
    '600': '#007FB2',
    '700': '#00658E',
    '800': '#004C6C',
    '900': '#00344C',
    '950': '#001E2E',
});
// --- Standalone colors (outside the neutral ramp) --------------------------
const COLOR_WHITE_src = { $value: '#FFFFFF', $type: 'color' };
const COLOR_BLACK_src = { $value: '#000000', $type: 'color' };
// --- Overlays (8-digit #RRGGBBAA) ------------------------------------------
// Alpha bytes: 5 -> 0D, 10 -> 1A, 20 -> 33, 40 -> 66, 60 -> 99, 80 -> CC.
const OVERLAY_WHITE_src = leaves('color', {
    '0': '#FFFFFF00',
    '5': '#FFFFFF0D',
    '10': '#FFFFFF1A',
    '20': '#FFFFFF33',
    '40': '#FFFFFF66',
    '60': '#FFFFFF99',
    '80': '#FFFFFFCC',
});
const OVERLAY_BLACK_src = leaves('color', {
    '0': '#00000000',
    '5': '#0000000D',
    '10': '#0000001A',
    '20': '#00000033',
    '40': '#00000066',
    '60': '#00000099',
    '80': '#000000CC',
});
// --- Spacing, radius, typography, shadow, blur -----------------------------
// Figma calls the radius collection "corner radius"; the description lookups use
// that path even though the repo group is named `radius`.
const SPACING_src = described('spacing', leaves('dimension', {
    '2': 2,
    '4': 4,
    '8': 8,
    '12': 12,
    '16': 16,
    '20': 20,
    '24': 24,
    '32': 32,
    '40': 40,
    '48': 48,
    '64': 64,
    '96': 96,
    '128': 128,
}), FIGMA_DESCRIPTIONS);
const RADIUS_src = described('corner radius', leaves('dimension', {
    '0': 0,
    '2': 2,
    '4': 4,
    '8': 8,
    '12': 12,
    '16': 16,
    '20': 20,
    '24': 24,
    '32': 32,
    '40': 40,
    full: 1000,
}), FIGMA_DESCRIPTIONS);
const FONT_WEIGHT_src = leaves('fontWeight', {
    '400': 400,
    '500': 500,
    '600': 600,
    '700': 700,
});
const FONT_SIZE_src = described('typography/size', leaves('dimension', {
    '11': 11,
    '12': 12,
    '13': 13,
    '14': 14,
    '16': 16,
    '20': 20,
    '24': 24,
    '28': 28,
    '48': 48,
}), FIGMA_DESCRIPTIONS);
const LINE_HEIGHT_src = described('typography/lineHeight', leaves('dimension', {
    '12': 12,
    '14': 14,
    '16': 16,
    '18': 18,
    '20': 20,
    '22': 22,
    '24': 24,
    '28': 28,
    '32': 32,
    '36': 36,
    '56': 56,
}), FIGMA_DESCRIPTIONS);
const FONT_FAMILY_PRIMARY_src = {
    $value: 'Figtree',
    $type: 'fontFamily',
};
const SHADOW_src = described('shadow', leaves('dimension', { xs: 4, sm: 8, md: 16, lg: 24 }), FIGMA_DESCRIPTIONS);
const BLUR_src = described('blur', leaves('dimension', { xs: 4, sm: 8, md: 16, lg: 24, xl: 36 }), FIGMA_DESCRIPTIONS);
// --- Public surface — lowercase, ergonomic for TS consumers. ---------------
export const PRIMITIVES = {
    color: {
        neutral: publicValues(COLOR_NEUTRAL_src),
        blue: publicValues(COLOR_BLUE_src),
        purple: publicValues(COLOR_PURPLE_src),
        red: publicValues(COLOR_RED_src),
        orange: publicValues(COLOR_ORANGE_src),
        green: publicValues(COLOR_GREEN_src),
        cyan: publicValues(COLOR_CYAN_src),
        white: COLOR_WHITE_src.$value,
        black: COLOR_BLACK_src.$value,
    },
    overlay: {
        white: publicValues(OVERLAY_WHITE_src),
        black: publicValues(OVERLAY_BLACK_src),
    },
    spacing: publicValues(SPACING_src),
    radius: publicValues(RADIUS_src),
    typography: {
        family: { primary: FONT_FAMILY_PRIMARY_src.$value },
        weight: publicValues(FONT_WEIGHT_src),
        size: publicValues(FONT_SIZE_src),
        lineHeight: publicValues(LINE_HEIGHT_src),
    },
    shadow: publicValues(SHADOW_src),
    blur: publicValues(BLUR_src),
};
// --- Exporter tree — PascalCase containers, shares the same _src groups. ---
// Registered under the top-level `Primitives` key of TOKENS in Task 4.
export const TOKENS_PRIMITIVES = {
    Color: {
        Neutral: COLOR_NEUTRAL_src,
        Blue: COLOR_BLUE_src,
        Purple: COLOR_PURPLE_src,
        Red: COLOR_RED_src,
        Orange: COLOR_ORANGE_src,
        Green: COLOR_GREEN_src,
        Cyan: COLOR_CYAN_src,
        // white/black are standalone swatches in Figma, not ramps, so they keep
        // their lowercase names alongside the PascalCase ramp groups above.
        white: COLOR_WHITE_src,
        black: COLOR_BLACK_src,
    },
    Overlay: {
        White: OVERLAY_WHITE_src,
        Black: OVERLAY_BLACK_src,
    },
    Spacing: SPACING_src,
    Radius: RADIUS_src,
    Typography: {
        Family: { primary: FONT_FAMILY_PRIMARY_src },
        Weight: FONT_WEIGHT_src,
        Size: FONT_SIZE_src,
        LineHeight: LINE_HEIGHT_src,
    },
    Shadow: SHADOW_src,
    Blur: BLUR_src,
};
//# sourceMappingURL=index.js.map